import { apiRequest, apiRequestWithEtag, type IEtagged } from '@/domains/api';
import type { ContentPostStatus } from './post-contracts';
import type { IContentTeamMember, IContentTeamMemberBody } from './team-contracts';

/**
 * survey-api's team page — `/api/v1/admin/website/team` — and the sole reader of it.
 *
 * It follows `posts-api.ts` in everything the two surfaces share: the revision travels as an
 * opaque `ETag` quoted back in `If-Match`, the server filters, and nothing here decides whether a
 * person may be published.
 *
 * **`reorder` is the one write whose precondition is in the body**: the arrangement belongs to the
 * page, not to any one person, so there is no single revision to quote, and the order the screen
 * moved from (`readOrder`) stands in for one. The body carries the *whole* live order, so the page
 * can never be left half-sorted. A list that no longer matches the page, or a `readOrder` somebody
 * else's reorder has overtaken, is refused with 409 `invalid-transition` rather than guessed at.
 * It answers the page as it now stands, which is what the screen redraws from.
 */

const BASE = '/api/v1/admin/website/team';

function memberPath(memberId: string): string {
  return `${BASE}/${encodeURIComponent(memberId)}`;
}

function precondition(etag: string): { 'If-Match': string } {
  return { 'If-Match': etag };
}

export const teamApi = {
  /** Everybody: the live in page order, the archived after them. */
  list(status?: ContentPostStatus, signal?: AbortSignal): Promise<IContentTeamMember[]> {
    return apiRequest<IContentTeamMember[]>(`${BASE}${status ? `?status=${status}` : ''}`, {
      signal
    });
  },

  read(memberId: string, signal?: AbortSignal): Promise<IEtagged<IContentTeamMember>> {
    return apiRequestWithEtag<IContentTeamMember>(memberPath(memberId), { signal });
  },

  /** Adds somebody as a draft, last among the live. */
  create(body: IContentTeamMemberBody): Promise<IEtagged<IContentTeamMember>> {
    return apiRequestWithEtag<IContentTeamMember>(BASE, { method: 'POST', body });
  },

  save(
    memberId: string,
    etag: string,
    body: IContentTeamMemberBody
  ): Promise<IEtagged<IContentTeamMember>> {
    return apiRequestWithEtag<IContentTeamMember>(memberPath(memberId), {
      method: 'PUT',
      body,
      headers: precondition(etag)
    });
  },

  publish(memberId: string, etag: string): Promise<IEtagged<IContentTeamMember>> {
    return apiRequestWithEtag<IContentTeamMember>(`${memberPath(memberId)}/publication`, {
      method: 'POST',
      headers: precondition(etag)
    });
  },

  /**
   * Brings somebody archived back to the working list **as a draft**, last among the live.
   *
   * Not onto the site: an archived person cannot be edited, so a return that published them
   * would mean putting whatever took them off the page back in front of readers in order to fix
   * it. survey-api refuses to publish an archived person for the same reason, so this is the
   * only way back — and it is `content.author`, because a draft is not public.
   */
  restore(memberId: string, etag: string): Promise<IEtagged<IContentTeamMember>> {
    return apiRequestWithEtag<IContentTeamMember>(`${memberPath(memberId)}/restoration`, {
      method: 'POST',
      headers: precondition(etag)
    });
  },

  /** WEB-07 AC4 — off the page, every word kept, and the rest close the gap. */
  archive(memberId: string, etag: string): Promise<IEtagged<IContentTeamMember>> {
    return apiRequestWithEtag<IContentTeamMember>(`${memberPath(memberId)}/publication`, {
      method: 'DELETE',
      headers: precondition(etag)
    });
  },

  /**
   * Deletes somebody who was **never** on the page — a row added by mistake.
   *
   * survey-api refuses it for anybody who ever appeared there, archived now or not: WEB-07 AC4
   * wants a departure kept, and a draft nobody ever saw has no departure. So this is not a
   * second way to remove a person; it is the only way to undo an "Add a person" that should not
   * have happened.
   */
  remove(memberId: string, etag: string): Promise<void> {
    return apiRequest<void>(memberPath(memberId), {
      method: 'DELETE',
      headers: precondition(etag)
    });
  },

  revert(memberId: string, etag: string, versionNo: number): Promise<IEtagged<IContentTeamMember>> {
    return apiRequestWithEtag<IContentTeamMember>(`${memberPath(memberId)}/reverts`, {
      method: 'POST',
      body: { versionNo },
      headers: precondition(etag)
    });
  },

  /** The whole live arrangement, in the order the page is to be drawn in, and the one it was
   *  moved from. Answers the page. */
  reorder(memberIds: string[], readOrder: string[]): Promise<IContentTeamMember[]> {
    return apiRequest<IContentTeamMember[]>(`${BASE}/order`, {
      method: 'PUT',
      body: { memberIds, readOrder }
    });
  }
};
